// Moneda de PRESENTACION de una cotización Datec.
//
// El motor de cálculo (`lib/calc.ts`) trabaja siempre en dólares. Acá solo se
// decide cómo se muestra un total ya calculado. Nada de esto entra en ninguna
// fórmula.

import { esTcPlausible } from './reference-rates';

export type Moneda = 'USD' | 'BOB';

/**
 * Si la cotización se puede presentar en la moneda pedida.
 *
 * En bolivianos hace falta un tipo de cambio creíble. Sin él, convertir sería
 * inventar: mejor mostrar los dólares y que la pantalla avise.
 */
export function puedeConvertir(moneda: Moneda, tc: number | null): boolean {
  if (moneda === 'USD') return true;
  return esTcPlausible(tc);
}

/**
 * El tipo de cambio, escrito como se escribe un número en Bolivia.
 *
 * 🔴 Vive acá y no en cada pantalla porque ya se había desincronizado: la
 * leyenda en pantalla decía `11,52` y el PDF interpolaba el número crudo, así
 * que el documento que ve el cliente imprimía `Bs 11.5`. Es la línea de
 * trazabilidad del documento — el único dato con el que se puede reconstruir
 * el total— y tiene que decir lo mismo en los dos lados.
 */
export function fmtTc(tc: number): string {
  return tc.toFixed(2).replace('.', ',');
}

/** Convierte un monto en USD a la moneda de presentación. */
export function convertir(montoUsd: number, moneda: Moneda, tc: number | null): number {
  if (!puedeConvertir(moneda, tc) || moneda === 'USD') return montoUsd;
  return montoUsd * (tc as number);
}

/**
 * Formatea un monto en USD para mostrarlo en la moneda de presentación.
 *
 * Los dólares van con formato inglés (`$1,234.56`) porque es como se venían
 * mostrando; los bolivianos con formato local (`Bs 14.222,13`, que es
 * `1.234,56 USD × 11,52`).
 */
export function fmtMoneda(montoUsd: number, moneda: Moneda, tc: number | null): string {
  if (!isFinite(montoUsd) || isNaN(montoUsd)) return '—';

  const convertible = puedeConvertir(moneda, tc);
  const monto = convertir(montoUsd, moneda, tc);

  if (moneda === 'BOB' && convertible) {
    return `Bs ${new Intl.NumberFormat('es-BO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(monto)}`;
  }

  return `$${new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(monto)}`;
}

/**
 * Una cifra ya formateada (`$2,399.55`) para mostrarla al lado del prefijo de moneda (`USD 2,399.55`): con el prefijo,
 * el «$» sobra y no se dice dos veces (decisión de Sebastián, 17-oct). Es el formato de la cabecera del editor; el
 * diálogo de envío y el «Análisis interno» usan el mismo (ronda C, 18-oct).
 */
export function sinSimboloDolar(cifra: string): string {
  return cifra.replace(/^(-?)\$/, '$1');
}

/** `USD 2,399.55`: el prefijo de moneda y la cifra sin «$». Sin moneda, la cifra tal cual. */
export function conPrefijoDeMoneda(cifra: string, moneda?: string | null): string {
  return moneda ? `${moneda} ${sinSimboloDolar(cifra)}` : cifra;
}
